import { Checkout } from '@df/checkout/checkout.interfaces';
import { IAddressData } from '@df/session/api/address-book/address';

declare global {
    interface Window {
        Stripe?(publishableKey: string, options?: Stripe.InstanceOptions): Stripe.Instance;
    }
}

/**
 * Data needed/used for stripe express only.
 */
declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            stripe?: Stripe.IExpressConfig;
        }
    }
}
declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketData {
            stripe?: {
                isApplePayEnabled: boolean;
            };
        }
    }
}
export namespace Stripe {
    export const AUTH_ERROR_START = 'Authentication Required: ';

    export const AUTH_ERROR_MESSAGE = 'Authentication failed';

    export enum Endpoints {
        GetSetupIntent = 'stripe/getSetupIntent',
        GetPaymentsRequestAPIParams = 'stripe/GetPaymentsRequestAPIParams',
        EstimateCart = 'stripe/estimateCart',
        ApplyShipping = 'stripe/applyShipping',
        PlaceOrder = 'stripe/placeOrder'
    }

    export enum Scripts {
        JS = 'https://js.stripe.com/v3/'
    }

    export enum PaymentMethod {
        StripePayments = 'stripe_payments',
        StripePaymentsPrapi = 'stripe_payments_prapi',
        StripePaymentsPaypal = 'stripe_payments_paypal'
    }

    export enum Component {
        Card = 'card',
        CardNumber = 'cardNumber',
        CardExpiry = 'cardExpiry',
        CardCvc = 'cardCvc',
        FpxBank = 'fpxBank',
        Iban = 'iban',
        IdealBank = 'idealBank',
        P24Bank = 'p24Bank',
        EpsBank = 'epsBank',
        PaymentRequestButton = 'paymentRequestButton',
        AuBankAccount = 'auBankAccount',
        AfterpayClearpayMessage = 'afterpayClearpayMessage',
        Payment = 'payment'
    }

    export enum PaymentType {
        AcssDebit = 'acss_debit',
        AfterpayClearpay = 'afterpay_clearpay',
        Alipay = 'alipay',
        AuBecsDEbit = 'au_becs_debit',
        BacsDebit = 'bacs_debit',
        Bancontact = 'bancontact',
        Boleto = 'boleto',
        Card = 'card',
        Eps = 'eps',
        Fpx = 'fpx',
        Giropay = 'giropay',
        Grabpay = 'grabpay',
        Ideal = 'ideal',
        Klarna = 'klarna',
        Oxxo = 'oxxo',
        P24 = 'p24',
        Paypal = 'paypal',
        SepaDebit = 'sepa_debit',
        Sofort = 'sofort',
        WechatPay = 'wechat_pay'
    }

    type NeverAuto = 'never' | 'auto';

    type NeverAutoAlways = 'never' | 'auto' | 'always';

    export enum ElementEvent {
        Change = 'change',
        Ready = 'ready',
        Focus = 'focus',
        Blur = 'blur',
        Escape = 'escape',
        Click = 'click'
    }

    export enum CardTypes {
        Visa = 'visa',
        Amex = 'amex',
        MasterCard = 'mastercard',
        Discover = 'discover',
        Diners = 'diners',
        Jcb = 'jcb',
        UnionPay = 'unionpay'
    }

    //////////////////////////////////////////
    /////
    /////   Payment method
    /////
    //////////////////////////////////////////

    export interface ICheckoutPaymentMethod<T extends ICheckoutPaymentMethodConfig = ICheckoutPaymentMethodConfig>
        extends Checkout.IPaymentMethod {
        config?: T;
    }

    export interface ICheckoutPaymentMethodConfig {
        initParams?: IInitParams;
    }

    export interface IInitParams {
        apiKey?: string;
        locale?: string;
        useSetupIntents?: boolean;
    }

    export interface ICardPaymentMethod extends ICheckoutPaymentMethod {}

    export interface IPaymentRequestButtonPaymentMethod extends ICheckoutPaymentMethod<IPaymentRequestButtonPaymentMethodConfig> {}

    export interface IPaymentRequestButtonPaymentMethodConfig extends ICheckoutPaymentMethodConfig {
        isApplePayEnabled?: boolean;
        buttonConfig?: IPaymentRequestButtonStylesOptions;
    }

    //////////////////////////////////////////
    /////
    /////   Stripe js
    /////
    //////////////////////////////////////////
    export interface Instance {
        elements(options: IElementsIntanceOptions): IElementsInstance;
        createPaymentMethod<T extends IPaymentMethod>(data: ICreatePaymentMethodData): Promise<ICreatePaymentMethodResponse<T>>;
        confirmCardPayment(
            clientSecret: string,
            data?: IConfirmCardData,
            options?: IConfirmCardOptions
        ): Promise<IConfirmCardPaymentResponse>;
        confirmCardSetup(clientSecret: string, data?: IConfirmCardData, options?: IConfirmCardOptions): Promise<IConfirmCardSetupResponse>;
        retrievePaymentIntent(clientSecret: string): Promise<IRetrievePaymentIntentResponse>;
        handleCardAction(clientSecret: string): Promise<IRetrievePaymentIntentResponse>;
        handleCardPayment(clientSecret: string): Promise<IRetrievePaymentIntentResponse>;
        paymentRequest(options: IPaymentRequestOptions): IPaymentRequestInstance;
    }

    export interface InstanceOptions {
        // For usage with Connect only
        stripeAccount?: string;
        // Override your account's API version.
        apiVersion?: string;
        // A locale used to globally configure localization in Stripe.
        // Setting the locale here will localize error strings for all Stripe.js methods.
        // It will also configure the locale for Elements and Checkout. Default is auto (Stripe detects the locale of the browser).
        locale?: string;
    }

    //////////////////////////////////////////
    /////
    /////   Billing details
    /////
    //////////////////////////////////////////

    export interface IBillingDetails {
        name?: string;
        email?: string;
        phone?: string;
        address?: IAddress;
    }

    export interface IAddress {
        city?: string;
        country?: string;
        line1?: string;
        line2?: string;
        postal_code?: string;
        state?: string;
    }

    //////////////////////////////////////////
    /////
    /////   Confirm card data
    /////
    //////////////////////////////////////////

    export interface IConfirmCardData {
        payment_method: string | ICardIntentData;
        return_url?: string;
    }

    export interface ICardIntentData {
        card?: ICardNumberElementInstance | ICardElementInstance;
        billing_details?: IBillingDetails;
    }

    export interface IConfirmCardOptions {
        handleActions?: boolean;
    }

    //////////////////////////////////////////
    /////
    /////   Confirm card setup
    /////
    //////////////////////////////////////////

    export interface IConfirmCardSetupResponse {
        error?: IError;
        setupIntent?: ISetupIntent;
    }

    export interface ISetupIntent {
        id: string;
        client_secret: string;
        customer?: string;
        description?: string;
        status?: string;
        payment_method?: string;
        payment_method_types?: string[];
        usage?: string;
    }

    //////////////////////////////////////////
    /////
    /////   Confirm card payment
    /////
    //////////////////////////////////////////

    export interface IConfirmCardPaymentResponse {
        error?: IError;
        paymentIntent: IPaymentIntent;
    }

    export interface IPaymentIntent {
        id: string;
        amount: number;
        client_secret: string;
        currency: string;
        customer?: string;
        description?: string;
        status?: 'requires_action' | 'requires_source_action';
        next_action?: {
            redirect_to_url?: string;
        };
        payment_method?: string;
        payment_method_types?: string[];
        confirmation_method?: 'manual' | 'automatic';
    }

    //////////////////////////////////////////
    /////
    /////   Create payment method
    /////
    //////////////////////////////////////////

    export interface ICreatePaymentMethodData extends ICardIntentData {
        type: PaymentType;
    }

    export interface ICreatePaymentMethodResponse<T extends IPaymentMethod> {
        error?: IError;
        paymentMethod?: T;
    }

    export interface IPaymentMethod {
        id: string;
        billing_details: IBillingDetails;
        customer?: string;
        metadata?: { [key: string]: string };
        type: PaymentType;
    }

    export interface ISource {
        id: string;
        amount: number;
        currency: string;
        customer: string;
        metadata?: { [key: string]: string };
        statement_descriptor: string;
        status: Status;
        type: PaymentType;
    }

    export interface IShippingAddress {
        country: string;
        addressLine: string[];
        region: string;
        city: string;
        postalCode: string;
        recipient: string;
        phone: string;
        sortingCode: string;
        dependentLocality: string;
        organization?: string;
    }

    export interface IBillingAddress {
        city: string;
        country: string;
        line1: string;
        line2: string;
        postal_code: string;
        state: string;
    }

    // Only chargeable sources can be used to create a charge.
    export type Status = 'canceled' | 'chargeable' | 'consumed' | 'failed' | 'pending';

    export interface ICardPaymentMethod extends IPaymentMethod {
        card: IPaymentMethodCard;
    }

    export interface IPaymentMethodCard {
        brand: CardTypes | 'unkown';
        checks?: {
            address_line1_check?: string;
            address_postal_code_check?: string;
            cvc_check?: string;
        };
        country: string;
        exp_month: string;
        exp_year: string;
        fingerprint: string;
        funding: 'credit' | 'debit' | 'prepaid' | 'unkown';
        last4: string;
        networks: {
            available: string[];
            preferred: string;
        };
        three_d_secure_usage: {
            supported: boolean;
        };
    }

    //////////////////////////////////////////
    /////
    /////   Retrieve payment intent
    /////
    //////////////////////////////////////////

    export interface IRetrievePaymentIntentResponse {
        paymentIntent: IPaymentIntent;
        error?: IError;
    }

    //////////////////////////////////////////
    /////
    /////   Elements
    /////
    //////////////////////////////////////////

    export interface IElementsInstance {
        submit(): void;
        update(options: IUpdateOptions): void;
        create(type: Component, options?: ICreateOptions): IElementInstance;
        create(type: Component.Payment, options?: ICreatePaymentOptions): IPaymentElementInstance;
        create(type: Component.Card, options?: ICreateCardOptions): ICardElementInstance;
        create(type: Component.CardNumber, options?: ICreateCardNumberOptions): ICardNumberElementInstance;
        create(type: Component.CardExpiry, options?: ICreateCardExpiryOptions): ICardExpiryElementInstance;
        create(type: Component.CardCvc, options?: ICreateCardCvcOptions): ICardCvcElementInstance;
        create(type: Component.PaymentRequestButton, options: ICreatePaymentRequestButtonOptions): IPaymentRequestButtonElementInstance;
        // This method looks up a previously created Element by its type.
        getElement(type: Component): IElementInstance | null;
    }

    export interface IElementsIntanceOptions {
        fonts?: (ICssFontSource | ICustomFontSource)[];
        locale?: string;
        mode?: 'payment' | 'setup';
        paymentMethodCreation?: 'automatic' | 'manual';
        amount?: number;
        currency?: string;
        // used with payment element
        // Match the design of your site with the appearance option.
        // The layout of each Element stays consistent, but you can modify colors, fonts, borders, padding, and more.
        appearance?: CSSStyleDeclaration;
        // Requried to use with the Payment Element.
        clientSecret?: string;
    }

    export interface ICssFontSource {
        // A relative or absolute URL pointing to a CSS file with @font-face definitions, for example:
        cssSrc?: string;
    }

    export interface ICustomFontSource {
        // The name to give the font.
        family: string;
        // A valid src value pointing to your custom font file.
        // This is usually (though not always) a link to a file with a .woff , .otf, or .svg suffix.
        src: string;
        // A valid font-display value.
        display?: string;
        // One of normal, italic, oblique. Defaults to normal.
        style?: string;
        // A valid unicode-range value.
        unicodeRange?: string;
        // A valid font-weight. Note that this is a string, not a number.
        weight?: string;
    }

    export interface IUpdateOptions {
        locale?: string;
        appearance?: CSSStyleDeclaration;
    }

    export interface ICreatePaymentOptions {
        // Provide information about your business that will be displayed in the Payment Element.
        // This information will be retrieved from your Stripe account if not provided.
        business?: {
            name?: string;
        };
        paymentMethodOrder?: string[];
        fields?: {
            billingDetails?: NeverAuto | ICreatePaymentOptionsBillingDetails;
        };
        terms?: ICreatePaymentTerms;
    }

    export interface ICreatePaymentOptionsBillingDetails {
        name?: NeverAuto;
        email?: NeverAuto;
        phone?: NeverAuto;
        address?: NeverAuto | ICreatePaymentOptionsBillingDetailsAddress;
    }

    export interface ICreatePaymentOptionsBillingDetailsAddress {
        line1?: NeverAuto;
        line2?: NeverAuto;
        city?: NeverAuto;
        state?: NeverAuto;
        country?: NeverAuto;
        postalCode?: NeverAuto;
    }

    export interface ICreatePaymentTerms {
        auBecsDebit?: NeverAutoAlways;
        bancontact?: NeverAutoAlways;
        card?: NeverAutoAlways;
        ideal?: NeverAutoAlways;
        sepaDebit?: NeverAutoAlways;
        sofort?: NeverAutoAlways;
        usBankAccount?: NeverAutoAlways;
    }

    export interface IElementInstance {
        // Updates the options the Element was initialized with. Updates are merged into the existing configuration.
        // If you collect certain information in a different part of your interface (e.g., ZIP or postal code),
        // use element.update with the appropriate information.
        // The styles of an Element can be dynamically changed using element.update.
        // This method can be used to simulate CSS media queries that automatically adjust the size of elements when viewed on different devices.
        update(options: {}): void;
        mount(domElement: string | HTMLElement): void;
        blur(): void;
        clear(): void;
        destroy(): void;
        focus(): void;
        unmount(): void;
        on(eventType: ElementEvent, callback: (event) => void): void;
    }

    export interface IElementInstaceClickEvent {
        // Calling this function synchronously prevents the browser's payment interface from being shown.
        // This can be used to validate the form before the payment interface is shown.
        preventDefault(): void;
    }

    export interface ICardElementInstance extends IElementInstance {
        update(options: ICreateCardOptions): void;
        on(eventType: ElementEvent.Change, callback: (event: ICardElementChangeEvent) => void): void;
    }

    export interface ICardNumberElementInstance extends IElementInstance {
        update(options: ICreateCardNumberOptions): void;
        on(eventType: ElementEvent.Change, callback: (event: ICardNumberElementChangeEvent) => void): void;
    }

    export interface ICardExpiryElementInstance extends IElementInstance {
        update(options: ICreateCardExpiryOptions): void;
        on(eventType: ElementEvent.Change, callback: (event: ICardPartElementChangeEvent) => void): void;
    }

    export interface ICardCvcElementInstance extends IElementInstance {
        update(options: ICreateCardCvcOptions): void;
        on(eventType: ElementEvent.Change, callback: (event: ICardPartElementChangeEvent) => void): void;
    }

    export interface IPaymentElementInstance extends IElementInstance {
        // This method collapses the Payment Element into a row of payment method tabs.
        collapse(): void;
        update(options: ICreatePaymentOptions): void;
        on(eventType: ElementEvent.Change, callback: (event: IPaymentElementChangeEvent) => void): void;
    }

    export interface ICreateOptions {
        // Set custom class names on the container DOM element when the Stripe element is in a particular state.
        classes?: IClasses;
        style?: IStyleObject;
        iconStyle?: 'solid' | 'default';
        disabled?: boolean;
    }

    export interface ICreateCardOptions extends ICreateOptions {
        // A pre-filled set of values to include in the input (e.g., {postalCode: '94110'}).
        // Note that sensitive card information (card number, CVC, and expiration date) cannot be pre-filled
        value?: string;
        hidePostalCode?: boolean;
        hideIcon?: boolean;
    }

    export interface ICreateCardNumberOptions extends ICreateOptions {
        placeholder?: string;
        showIcon?: boolean;
    }

    export interface ICreateCardExpiryOptions extends ICreateOptions {
        placeholder?: string;
    }

    export interface ICreateCardCvcOptions extends ICreateOptions {
        placeholder?: string;
        showIcon?: boolean;
    }

    export interface IClasses {
        // The base class applied to the container. Defaults to StripeElement.
        base?: string;
        // The class name to apply when the Element is complete. Defaults to StripeElement--complete.
        complete?: string;
        // The class name to apply when the Element is empty. Defaults to StripeElement--empty.
        empty?: string;
        // The class name to apply when the Element is focused. Defaults to StripeElement--focus.
        focus?: string;
        // The class name to apply when the Element is invalid. Defaults to StripeElement--invalid.
        invalid?: string;
        // The class name to apply when the Element has its value autofilled by the browser (only on Chrome and Safari).
        // Defaults to StripeElement--webkit-autofill.
        webkitAutofill?: string;
    }

    export interface IStyleObject {
        base?: IStyleObjectProperties;
        complete?: IStyleObjectProperties;
        empty?: IStyleObjectProperties;
        invalid?: IStyleObjectProperties;
    }

    export interface IStyleObjectProperties {
        backgroundColor?: string;
        color?: string;
        fontFamily?: string;
        fontSize?: string;
        fontSmoothing?: string;
        fontStyle?: string;
        fontVariant?: string;
        fontWeight?: string;
        iconColor?: string;
        lineHeight?: string;
        letterSpacing?: string;
        textAlign?: string;
        padding?: string;
        textDecoration?: string;
        textShadow?: string;
        textTransform?: string;
    }

    export interface IElementChangeEvent {
        elementType: Component;
        empty: boolean;
        // true if all required fields for the selected payment method in element have been filled with potentially valid input.
        complete: boolean;
    }

    export interface IPaymentElementChangeEvent extends IElementChangeEvent {
        collapsed?: boolean;
        value: { type: string };
    }

    export interface ICardElementChangeEvent extends IElementChangeEvent {
        error?: IError;
        brand?: CardTypes | string;
        value: { postalCode?: string };
    }

    export interface ICardPartElementChangeEvent extends IElementChangeEvent {
        error?: IError;
    }

    export interface ICardNumberElementChangeEvent extends ICardPartElementChangeEvent {
        brand?: CardTypes;
    }

    export interface IError {
        code: string;
        type: string;
        message: string;
    }

    //////////////////////////////////////////
    /////
    /////   Get client secret
    /////
    //////////////////////////////////////////

    export interface IGetClientSecretData {
        customer_data: {
            guestEmail?: string;
            billingAddress: IAddressData;
        };
    }

    export interface IGetClientSecretResponse {
        client_secret: string;
    }

    export interface ICreateElementsDependences {
        total?: number;
        clientSecretData: IGetClientSecretData;
    }

    //////////////////////////////////////////
    /////
    /////   Submission data
    /////
    //////////////////////////////////////////

    export interface ICardSubmissionData extends Checkout.ISubmissionPaymentData {
        method: PaymentMethod.StripePayments;
        additional_data: ICardSubmissionAdditionalData;
    }

    export interface ICardSubmissionAdditionalData {
        cc_stripejs_token: string;
        cc_save?: boolean;
    }

    export interface IPaymentRequestButtonSubmissionData extends Checkout.ISubmissionPaymentData {
        method: PaymentMethod.StripePaymentsPrapi;
        additional_data: {
            result: IPaymentRequestResponse;
            location: 'checkout';
        };
    }

    //////////////////////////////////////////
    /////
    /////   Payment Request
    /////
    //////////////////////////////////////////

    export interface IGetPaymentsRequestAPIParamsData {
        type: 'checkout' | 'cart';
    }

    export interface IPaymentRequestOptions {
        country: string;
        currency: string;
        displayItems?: IPaymentRequestOptionsLine[];
        requestPayerEmail?: boolean;
        requestPayerName?: boolean;
        requestPayerPhone?: boolean;
        requestShipping?: boolean;
        total: IPaymentRequestOptionsLine;
        shippingOptions?: IPaymentRequestShippingOption[];
        disableWallets?: IPaymentRequestWallet[];
    }

    export type IPaymentRequestWallet = 'applePay' | 'googlePay' | 'browserCard' | 'link';

    export type IPaymentRequestMethod = 'apple-pay' | 'google-pay' | 'browser-card' | 'link';

    export interface IPaymentRequestShippingOption {
        id?: string;
        label?: string;
        detail?: string;
        amount?: number;
    }

    export interface IPaymentRequestOptionsLine {
        label: string;
        amount: number;
        pending?: boolean;
    }

    export enum PaymentRequestEvent {
        token = 'token',
        paymentmethod = 'paymentmethod',
        source = 'source',
        cancel = 'cancel',
        shippingaddresschange = 'shippingaddresschange',
        shippingoptionchange = 'shippingoptionchange'
    }

    export interface IPaymentRequestInstance {
        canMakePayment(): Promise<IPaymentRequestInstanceCanMakePaymentResult>;
        update(updateOptions: IPaymentRequestInstanceUpdateOptions): void;
        on(event: PaymentRequestEvent.token, handler: (event: IPaymentRequestResponse) => void): void;
        on(event: PaymentRequestEvent.paymentmethod, handler: (event: IPaymentRequestResponse) => void): void;
        on(event: PaymentRequestEvent.source, handler: (event: IPaymentRequestResponse) => void): void;
        // The cancel event is emitted from a PaymentRequest when the browser‘s payment interface is dismissed.
        // Note that in some browsers, the payment interface may be dismissed by the customer even after they authorize the payment.
        // This means that you may receive a cancel event on your PaymentRequest object after receiving a token,
        // paymentmethod, or source event. If you’re using the cancel event as a hook for canceling the customer’s order,
        // make sure you also refund the payment that you just created.
        on(event: PaymentRequestEvent.cancel, handler: () => void): void;
        on(event: PaymentRequestEvent.shippingaddresschange, handler: (event: IPaymentRequestShippingAddressChangeEvent) => void): void;
        on(event: PaymentRequestEvent.shippingoptionchange, handler: (event: IPaymentRequestShippingOptionChangeEvent) => void): void;
    }

    export interface IPaymentRequestInstanceCanMakePaymentResult {
        applePay?: boolean;
        googlePay?: boolean;
    }

    export interface IPaymentRequestInstanceUpdateOptions {
        currency?: string;
        total?: IPaymentRequestOptionsLine;
        displayItems?: IPaymentRequestOptionsLine[];
        shippingOptions?: IPaymentRequestShippingOption[];
    }

    export interface IPaymentRequestResponse {
        token: IPaymentRequestResponseToken;
        paymentMethod: IPaymentMethod;
        source: ISource;
        payerName: string;
        payerEmail: string;
        payerPhone: string;
        shippingOption: IPaymentRequestShippingOption;
        shippingAddress: IShippingAddress;
        methodName?: IPaymentRequestMethod;
        walletName: IPaymentRequestWallet;
        //  is a Stripe.js provided function. Call this when you have processed the token data provided by the API.
        // Note that you must must call complete within 30 seconds.
        complete(status: PaymentRequestCompleteStatus): void;
    }

    export interface IPaymentRequestResponseToken {
        id: string;
        object: 'token';
    }

    export enum PaymentRequestCompleteStatus {
        success = 'success',
        fail = 'fail',
        invalidPayerName = 'invalid_payer_name',
        invalidPayerPhone = 'invalid_payer_phone',
        invalidPayerEmail = 'invalid_payer_email',
        invalidShippingAddress = 'invalid_shipping_address'
    }

    export interface IPaymentRequestShippingAddressChangeEvent {
        shippingAddress: IShippingAddress;
        updateWith(updateDetails: IPaymentRequestUpdateDetails): void;
    }

    export interface IPaymentRequestUpdateDetails {
        status: 'success' | 'fail' | 'invalid_shipping_address';
        total?: IPaymentRequestOptionsLine;
        displayItems?: IPaymentRequestOptionsLine[];
        shippingOptions?: IPaymentRequestShippingOption[];
    }

    export interface IPaymentRequestShippingOptionChangeEvent {
        shippingOption: IPaymentRequestShippingOption;
        updateWith(updateDetails: IPaymentRequestUpdateDetails): void;
    }

    export interface ICreatePaymentRequestButtonOptions {
        paymentRequest: IPaymentRequestInstance;
        // Set custom class names on the container DOM element when the Stripe element is in a particular state.
        classes?: IClasses;
        style?: {
            paymentRequestButton: IPaymentRequestButtonStylesOptions;
        };
    }

    export interface IPaymentRequestButtonElementInstance extends IElementInstance {
        update(options: ICreatePaymentRequestButtonOptions): void;
        on(eventType: ElementEvent, callback: (event) => void): void;
        on(eventType: ElementEvent.Click, callback: (event: IElementInstaceClickEvent) => void): void;
    }

    export interface IPaymentRequestButtonStylesOptions {
        theme?: 'dark' | 'light' | 'light-outline';
        height?: string; // accepts px units
        type: IPaymentRequestButtonStyleType;
    }

    // Preferred button type to display. Available types, by wallet:
    // Browser card: default, book, buy, or donate.
    // Google Pay: default, buy, or donate.
    // Apple Pay: default, book, buy, donate, check-out, subscribe, reload, add-money, top-up, order, rent, support, contribute, tip
    // When a wallet does not support the provided value, default is used as a fallback.
    export type IPaymentRequestButtonStyleType =
        | 'default'
        | 'book'
        | 'buy'
        | 'donate'
        | 'check-out'
        | 'subscribe'
        | 'reload'
        | 'add-money'
        | 'top-up'
        | 'order'
        | 'rent'
        | 'support'
        | 'contribute'
        | 'tip';

    export interface IPaymentRequestUpdateRequiredData {
        billingCountry?: string;
        total?: number;
        items: {
            amount: number;
            label?: string;
            id: number;
        }[];
    }

    //////////////////////////////////////////
    /////
    /////   Stripe Express
    /////
    //////////////////////////////////////////

    export interface IExpressConfig {
        initParams: IInitParams;
        buttonConfig: IPaymentRequestButtonStylesOptions;
    }

    export interface IExpressApplyShippingData {
        address: IShippingAddress;
        shipping_id: string;
    }

    export interface IExpressApplyShippingResult {
        results: IExpressApplyShippingResults;
    }

    export interface IExpressApplyShippingResults {
        displayItems: IPaymentRequestOptionsLine[];
        total: IPaymentRequestOptionsLine;
    }

    export interface IExpressEstimateCartData {
        address: IShippingAddress;
    }

    export interface IExpressEstimateCartResult {
        results: IPaymentRequestShippingOption[];
    }

    export interface IExpressPlaceOrderData {
        shippingOption?: IPaymentRequestShippingOption;
        shippingAddress: IShippingAddress;
        payerEmail: string;
        payerPhone: string;
        payerName: string;
        walletName: IPaymentRequestWallet;
        methodName?: IPaymentRequestMethod;
        paymentMethod: IPaymentMethod;
        location: 'cart';
    }

    //////////////////////////////////////////
    /////
    /////   Paypal
    /////
    //////////////////////////////////////////

    export interface IPaypalSubmissionData extends Checkout.ISubmissionPaymentData {
        method: Stripe.PaymentMethod;
        additional_data: IPaypalSubmissionAdditionalData;
    }

    export interface IPaypalSubmissionAdditionalData {
        payment_element: boolean;
        payment_method: string;
        manual_authentication: 'cart';
    }
    export interface IPaypalIntentData extends ICardIntentData {}

    export interface ICreatePaymentElementOptions extends ICreateOptions {
        layout?: string;
        paymentMethodOrder?: string[];
    }

    export interface IPaypalPaymentMethod extends ICheckoutPaymentMethod {}

    export const zeroDecimalCurrencies = [
        'BIF',
        'CLP',
        'DJF',
        'GNF',
        'JPY',
        'KMF',
        'KRW',
        'MGA',
        'PYG',
        'RWF',
        'UGX',
        'VND',
        'VUV',
        'XAF',
        'XOF',
        'XPF'
    ];
}
